import { useCallback, useEffect, useState } from "react";
import { 
    ITag, 
    Label, 
    Panel, 
    PanelType, 
    Slider, 
    TagPicker as OUITagPicker, 
    TextField, 
    Toggle 
} from "office-ui-fabric-react";
import { loadAllTeams } from "../../../api/userInfo";
import { IDesk } from "../../../types/Floorplan.types";
import { ITeam } from "../../../types/User.types";
import { TagPicker } from "../../TagPicker";
import { Tag, TagType } from "../../../types/Tagging.types";

export interface IDeskEditorPanel {
    onChange: (desk: IDesk) => void;
    onDismiss: () => void;
    snapping: boolean;
    value: IDesk;
}

const getTextFromItem = (item: ITag) => {
    return item.name;
}

const snapTo45 = (num: number) => {
    return Math.round(num / 45) * 45
};

const _listContainsTeamsTags = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.filter((compareTag) => compareTag.key === tag.key).length > 0;
}

export const DeskEditorPanel = (props: IDeskEditorPanel) => {
    const { onChange, value: desk, snapping } = props;
      
    const [ alias, setAlias ] = useState<string|undefined>(desk.alias);
    const [ rotation, setRotation ] = useState<number|undefined>(snapping ? snapTo45(desk.rotation) : desk.rotation);
    const [ bookable, setBookable ] = useState<boolean|undefined>(desk.bookable);
    const [ assignedTeamTag, setAssignedTeamTag ] = useState<ITag[]>([]);
    const [ deskTags, setDeskTags ] = useState<Tag[]|undefined>();
    const [ teamsTags, setTeamsTags] = useState<ITag[]>([]);
    const [ ready, setReady ] = useState(false);

    // Load Teams options and assigned tags
    const loadTeamOptions = useCallback(async () => {
        const teamsResponse = await loadAllTeams();
        if (teamsResponse.ok) {
            const teams = await teamsResponse.json() as ITeam[];
            const teamTagOptions:ITag[] = teams.map((s) => {
                return {
                    key: s.Id,
                    name: s.Name
                }
            });            
            setTeamsTags(teamTagOptions);
            const assignedTeamTag:ITag[] = [];
            if (desk.assignedTeams && desk.assignedTeams.length > 0) {
                for (let index = 0; index < teamTagOptions.length; index++) {
                    const element = teamTagOptions[index];
                    desk.assignedTeams.forEach((a) => {
                        if (a === element.key as number) {
                            assignedTeamTag.push(element)
                        }
                    });
                }
            }
            setAssignedTeamTag(assignedTeamTag);
        }
    }, [desk]);

    const loadTags = useCallback(() => {
        setDeskTags(desk.tags);
    }, [desk]);

    // When Teams Tag Picker is updated
    const onResolveTeamSuggestions = useCallback((filter: string, selectedItems?: ITag[]): ITag[] => {
        if (teamsTags) {
            return filter
            ? teamsTags
                .filter((tag) => tag.name.toLowerCase().indexOf(filter.toLowerCase()) === 0)
                .filter((tag) => !_listContainsTeamsTags(tag, selectedItems))
            : [];
        } else {
            return [];
        }
    }, [teamsTags]);

    // When bookable is changed
    const onBookableUpdated = (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean | undefined) => {
        if (!checked) {
            setAssignedTeamTag([]);
        }
        setBookable(checked);
    }

    // When form changes, update JSON and update parent component(s)
    const updateDesk = useCallback((update: IDesk) => {
        return {...desk, ...update};
    }, [desk]);
    useEffect(() => {
        if (ready) {
            const newDesk = {} as IDesk;
            if (alias !== undefined) newDesk.alias = alias;
            if (bookable !== undefined) newDesk.bookable = bookable;
            if (rotation !== undefined) newDesk.rotation = rotation;
            if (!newDesk.assignedTeams) newDesk.assignedTeams = [];
            if (assignedTeamTag !== undefined && assignedTeamTag.length > 0) {
                newDesk.assignedTeams.push(assignedTeamTag[0].key as number);
            } 
            newDesk.tags = deskTags || [];
            onChange(updateDesk(newDesk));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alias, assignedTeamTag, bookable, deskTags, rotation]);

    useEffect(() => {
        loadTeamOptions().then(() => {
            loadTags();
            setReady(true);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Panel type={PanelType.smallFixedFar} isOpen={true} headerText="Edit Desk" onDismiss={props.onDismiss}>
            <TextField autoFocus label="Name / Alias" value={alias} onChange={(e, newValue) => setAlias(newValue)} />
            <Slider label="Rotation" min={0} max={snapping ? 315 : 359} step={snapping ? 45 : 1} value={rotation} onChange={(value) => {
                setRotation(snapping ? snapTo45(value) : value);
            }} />
            <TagPicker 
                label="Tags"
                type={TagType.Desk}
                onChange={(tags) => {
                    if (ready) setDeskTags(tags);
                }}
                selectedTags={desk.tags}
            />
            <Toggle label="Bookable" onText="Yes" offText="No" checked={bookable} onChange={onBookableUpdated} />
            { bookable && (
                <div>
                    <Label>Assigned Team</Label>
                    <div style={{paddingBottom: 10, fontStyle: "italic"}}>Note: If you assign a Team to this desk only staff from that team can book it out.</div>
                    <OUITagPicker 
                        selectedItems={assignedTeamTag}
                        onResolveSuggestions={onResolveTeamSuggestions}
                        getTextFromItem={getTextFromItem}
                        pickerSuggestionsProps={{
                            noResultsFoundText: 'No teams found',
                            suggestionsHeaderText: 'Suggested Teams',
                        }}
                        inputProps={{
                            'aria-label': 'Teams Picker',
                        }}
                        onChange={(items?: ITag[]) => {
                            setAssignedTeamTag(items !== undefined ? items : []);                 
                        }}
                        itemLimit={1}
                    />
                </div>
            )}
        </Panel>
    );
}